import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { useDashboardState } from "components/DashboardContext";
import { useOnClickOutside } from 'utils/helpers';
import './SidebarButtonDropdown.scss';

import { ReactComponent as ArrowIcon } from "static/icons/arrow.svg";

const SidebarButtonDropdown = ({
  label,
  buttonTitle = "Select",
  disabled,
  children,
  dropdownPosition,
  dropdownVerticalPosition,
  isFixedHeight,
  isFullHeight,
  isClear,
  clearActive,
  isItemsEmpty,
  onOpenCallback,
  onCloseDropdown,
  dropDownCloseStatus,
  optionsType,
  onClearAll,
  isOpenInitialDropdown,
}) => {
  const ref = useRef();
  const dropdownRef = useRef();
  const dropdownButtonRef = useRef();

  const {
    checkSidebarDropdownStatus,
    setCheckSidebarDropdownStatus,
  } = useDashboardState();

  const [dropdownIsOpen, setDropdownIsOpen] = useState(isOpenInitialDropdown);
  const [marginOffset, setMarginOffset] = useState(0);
  const [positionTop, setPositionTop] = useState(0);

  useEffect(() => {
    if(!isOpenInitialDropdown) {
      setDropdownIsOpen(isOpenInitialDropdown);
    }
  }, [isOpenInitialDropdown]);

  useEffect(() => {
    if (dropDownCloseStatus) {
      setDropdownIsOpen(dropDownCloseStatus);
    }
  }, [dropDownCloseStatus]);

  useOnClickOutside(ref, () => {
    setDropdownIsOpen(false);

    if (dropdownIsOpen) {
      setMarginOffset(0);
    }

    onCloseDropdown?.(false);
  });

  const buttonDropdownStyles = classNames({
    'sidebar-button-dropdown': true,
    'sidebar-button-dropdown--left': dropdownPosition === 'left',
    'sidebar-button-dropdown--right': dropdownPosition === 'right',
    'sidebar-button-dropdown--fixed-height': isFixedHeight,
    'sidebar-button-dropdown--full-height': isFullHeight,
    'sidebar-button-dropdown--empty': isItemsEmpty,
    'sidebar-button-dropdown--top': dropdownVerticalPosition === 'top',
  });

  const handleDropdownIsOpen = () => {
    onOpenCallback(!dropdownIsOpen)
    setDropdownIsOpen(!dropdownIsOpen);

    if (!dropdownIsOpen) {
      setCheckSidebarDropdownStatus(true);
    } else {
      setMarginOffset(0);
    }

    const dropdownButtonPosition = dropdownButtonRef.current.getBoundingClientRect().top;
    setPositionTop(dropdownButtonPosition);
  };

  const handleClearAll = () => {
    onClearAll(optionsType)
  };

  useEffect(() => {
    if (checkSidebarDropdownStatus && dropdownIsOpen) {
      const height = dropdownRef?.current?.clientHeight;
      const offsetTop = dropdownRef?.current?.getBoundingClientRect();
      const windowInnerHeight = window.innerHeight;

      if (marginOffset) {
        if (height + offsetTop.top + 20 + marginOffset > windowInnerHeight) {
          setMarginOffset(height + offsetTop.top + marginOffset - windowInnerHeight);
        } else {
          setMarginOffset(0);
        }
      } else {
        if (height + offsetTop.top + 20 > windowInnerHeight) {
          setMarginOffset(height + offsetTop.top - windowInnerHeight);
        } else {
          setMarginOffset(0);
        }
      }

      setCheckSidebarDropdownStatus(false);
    }
  }, [checkSidebarDropdownStatus, dropdownIsOpen, marginOffset, setCheckSidebarDropdownStatus]);

  return (
    <div
      ref={ref}
      className={
        classNames({
          'sidebar-button-dropdown__container': true,
          'sidebar-button-dropdown__container--center': dropdownPosition === 'center',
        })
      }
    >
      <div className={"sidebar-button-dropdown__header"}>
        <p className={"sidebar-button-dropdown__header_label"}>
          {label}
        </p>

        {isClear && !disabled && clearActive && <p
          className={"sidebar-button-dropdown__header_clear"}
          onClick={handleClearAll}
        >
          clear
        </p>}
      </div>

      <div
        ref={dropdownButtonRef}
        className={classNames({
          "sidebar-button-dropdown__wrapper": true,
          "sidebar-button-dropdown__wrapper--active": dropdownIsOpen,
          "sidebar-button-dropdown__wrapper--disabled": disabled,
        })}
        onClick={!disabled ? handleDropdownIsOpen : undefined}
      >
        <p
          className={classNames({
            "sidebar-button-dropdown__wrapper_text": true,
            "sidebar-button-dropdown__wrapper_text--active": dropdownIsOpen,
          })}
        >
          {buttonTitle}
        </p>

        <ArrowIcon
          className={classNames({
            "sidebar-button-dropdown__wrapper_icon": true,
            "sidebar-button-dropdown__wrapper_icon--active": dropdownIsOpen,
          })}
        />
      </div>

      {dropdownIsOpen && !disabled &&
        <div
          ref={dropdownRef}
          className={buttonDropdownStyles}
          style={{
            marginTop: marginOffset ? -(marginOffset + 20) : '0',
            top: positionTop
          }}
        >
          {children}
        </div>
      }
    </div>
  );
};

export default SidebarButtonDropdown;
